import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface SmsStatus {
  configured: boolean;
  fromNumber: string | null;
}

interface SmsConfigData {
  enabled: boolean;
  sendOnEmergency: boolean;
  sendOnReviewerAssign: boolean;
  sendOnAnnouncement: boolean;
}

export function SmsConfigPanel() {
  const [status, setStatus] = useState<SmsStatus | null>(null);
  const [config, setConfig] = useState<SmsConfigData>({
    enabled: false,
    sendOnEmergency: true,
    sendOnReviewerAssign: false,
    sendOnAnnouncement: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      apiClient<SmsStatus>('/sms/status').then(setStatus).catch(() => setStatus({ configured: false, fromNumber: null })),
      apiClient<SmsConfigData>('/sms/config').then(setConfig).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await apiClient<SmsConfigData>('/sms/config', {
        method: 'PUT',
        body: config,
      });
      setConfig(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiClient<{ success: boolean; error?: string }>('/sms/test', {
        method: 'POST',
      });
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, error: err?.message || 'Request failed — check API logs' });
    } finally {
      setTesting(false);
    }
  };

  const toggleStyle: React.CSSProperties = {
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    border: 'none',
    position: 'relative',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    flexShrink: 0,
  };

  const toggleDot: React.CSSProperties = {
    position: 'absolute',
    top: '3px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  };

  const renderToggle = (value: boolean, onChange: (v: boolean) => void, label: string, desc?: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
      <button
        onClick={() => onChange(!value)}
        style={{ ...toggleStyle, backgroundColor: value ? '#1a6b3c' : '#ccc' }}
        aria-pressed={value}
        aria-label={`Toggle ${label}`}
      >
        <span style={{ ...toggleDot, left: value ? '23px' : '3px' }} />
      </button>
      <div>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{label}</span>
        {desc && <p style={{ fontSize: '0.75rem', color: '#666', margin: '2px 0 0 0' }}>{desc}</p>}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {loading && <p style={{ fontSize: '0.85rem', color: '#666' }}>Loading SMS configuration...</p>}
      {!loading && (
      <>
      {/* Connection status */}
      <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: status?.configured ? '#e8f5e9' : '#fff3e0', border: `1px solid ${status?.configured ? '#a5d6a7' : '#ffcc80'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{status?.configured ? '✅' : '⚠️'}</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {status?.configured ? `Twilio connected (${status.fromNumber})` : 'Twilio not configured'}
          </span>
        </div>
        {!status?.configured && (
          <p style={{ fontSize: '0.75rem', color: '#e65100', margin: '4px 0 0 0' }}>
            Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_PHONE_NUMBER in Railway environment variables.
          </p>
        )}
      </div>

      {/* Test SMS button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          onClick={handleTest}
          disabled={testing || !status?.configured}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: testing || !status?.configured ? 'not-allowed' : 'pointer',
            opacity: testing || !status?.configured ? 0.6 : 1,
          }}
        >
          {testing ? 'Sending...' : '📱 Send Test SMS to My Phone'}
        </button>
        {testResult && (
          <span style={{ fontSize: '0.8rem', color: testResult.success ? '#2e7d32' : '#d32f2f' }}>
            {testResult.success ? '✓ Test SMS sent!' : `✕ ${testResult.error}`}
          </span>
        )}
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '0.5rem 0' }} />

      {/* Config toggles */}
      <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333', margin: 0 }}>
        SMS Notification Rules
      </p>

      {renderToggle(config.enabled, (v) => setConfig({ ...config, enabled: v }), 'SMS Enabled', 'Master switch — disables all SMS when off')}
      {renderToggle(config.sendOnEmergency, (v) => setConfig({ ...config, sendOnEmergency: v }), 'Emergency Alerts', 'Send SMS for emergency notifications (Guest Experience zones)')}
      {renderToggle(config.sendOnReviewerAssign, (v) => setConfig({ ...config, sendOnReviewerAssign: v }), 'Reviewer Assignments', 'Send SMS when a booklet reviewer is assigned')}
      {renderToggle(config.sendOnAnnouncement, (v) => setConfig({ ...config, sendOnAnnouncement: v }), 'Announcements with "Send SMS"', 'When organizer checks "Also send as SMS" on an announcement')}

      {/* Save button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1a6b3c',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Config'}
        </button>
        {saved && <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>✓ Saved</span>}
        {error && <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>{error}</span>}
      </div>
      </>
      )}
    </div>
  );
}
